import { ArrowRight, Clock } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { CategoryChip } from "@/components/CategoryChip";
import { resolveBlogImage } from "@/lib/blog-images";
import { useArticleReader } from "@/components/ArticleReader";
import type { BlogPostListItem } from "@/lib/blog";

interface Props {
  post: BlogPostListItem;
}

export function BlogCard({ post }: Props) {
  const { lang, t } = useI18n();
  const { openArticle } = useArticleReader();
  const title = lang === "es" ? post.title_es : post.title_en;
  const excerpt = lang === "es" ? post.excerpt_es : post.excerpt_en;
  const date = new Date(post.published_at).toLocaleDateString(lang === "es" ? "es-MX" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const cardClass =
    "group flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-elegant)]";

  const inner = (
    <>
      <div className="relative aspect-[16/10] overflow-hidden bg-secondary">
        <img
          src={resolveBlogImage(post.cover_image_url)}
          alt={title}
          loading="lazy"
          width={1536}
          height={960}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3">
          <CategoryChip category={post.category} className="bg-background/90 backdrop-blur" />
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <time dateTime={post.published_at}>{date}</time>
          <span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {post.read_minutes} {t("blog.minRead")}
          </span>
        </div>
        <h3 className="font-serif text-xl font-bold leading-snug text-primary transition-colors group-hover:text-accent">
          {title}
        </h3>
        <p className="line-clamp-3 text-sm text-muted-foreground">{excerpt}</p>
        <div className="mt-auto inline-flex items-center gap-1.5 pt-2 text-sm font-semibold text-accent">
          {t("blog.readArticle")}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </>
  );



  return (
    <button
      type="button"
      onClick={() => openArticle(post.slug)}
      className={`${cardClass} w-full cursor-pointer text-left`}
      aria-label={title}
    >
      {inner}
    </button>
  );
}
